import { useEffect } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { Colors } from 'cio-design-library/design-tokens'
import { useI18n } from 'builder/hooks/useI18n'

import { DialogModalV2 } from 'builder/components/DialogModal'
import Button, { ButtonTheme } from 'builder/components/Button'
import { Icon20 } from 'builder/components/Icon'

import { goToBillingPlanPage } from 'builder/utils/goToBillingPlanPage'
import { useGetAddonPrice } from 'builder/hooks/useGetAddonPrice'
import { useUser } from 'builder/hooks/useUser'
import HourGlassSrc from './assets/hourglass.webp'
import {
  Cell,
  Container,
  Details,
  DetailsContainer,
  Img,
  Row,
  SlashedPrice,
  Table,
  PriceLoader,
} from './styles'

import PeopleSrc from './assets/people.webp'
const TRANSLATION = 'builder.auto_apply.non_supported_user_dual_modal'
interface Props {
  planName: string
  closeModal: () => void
  label?: string
}

export const NonSupportedUserAutoApplyResumeDistributionModal = ({
  closeModal,
  planName,
  label,
}: Props) => {
  const user = useUser()

  useEffect(() => {
    trackInternalEvent('show_auto_apply_upsell_modal', { label })
  }, [])

  const { i18n } = useI18n()

  const { upsellAmount, recurringAmount, addonAmount, subscriptionAmount, isLoading } =
    useGetAddonPrice({
      addon: planName,
      fetchPrice: true,
    })

  const getPriceButton = () => {
    if (isLoading)
      return (
        <>
          <div style={{ width: '10px' }} />
          <PriceLoader type="white" />
        </>
      )

    if (recurringAmount() === upsellAmount()) {
      return <>&nbsp;{upsellAmount()}</>
    }

    return (
      <>
        &nbsp;<SlashedPrice>{recurringAmount()}</SlashedPrice>&nbsp;{upsellAmount()}
      </>
    )
  }

  return (
    <DialogModalV2
      onClose={closeModal}
      title={i18n.t(`${TRANSLATION}.title`)}
      subtitle={i18n.t(`${TRANSLATION}.subtitle`)}
      disableAutoCloseOutside
    >
      <Container>
        <Table>
          <Row>
            <Cell isTitle borderRadiusString="border-top-left-radius: 10px;" />
            <Cell isTitle>{i18n.t(`${TRANSLATION}.table.col1_title`)}</Cell>
            <Cell
              isTitle
              color={Colors.White}
              bgColor={Colors.Indigo50}
              borderRadiusString="border-top-right-radius: 10px;"
            >
              {i18n.t(`${TRANSLATION}.table.col2_title`)}
            </Cell>
          </Row>

          <Row>
            <Cell>{i18n.t(`${TRANSLATION}.table.row1`)}</Cell>
            <Cell>
              <Icon20.Check />
            </Cell>
            <Cell color={Colors.Indigo70} bgColor={Colors.Indigo10}>
              <Icon20.Check />
            </Cell>
          </Row>
          <Row>
            <Cell>{i18n.t(`${TRANSLATION}.table.row2`)}</Cell>
            <Cell>
              <Icon20.Check />
            </Cell>
            <Cell color={Colors.Indigo70} bgColor={Colors.Indigo10}>
              <Icon20.Check />
            </Cell>
          </Row>
          <Row>
            <Cell>{i18n.t(`${TRANSLATION}.table.row3`)}</Cell>
            <Cell>
              <Icon20.Check />
            </Cell>
            <Cell color={Colors.Indigo70} bgColor={Colors.Indigo10}>
              <Icon20.Check />
            </Cell>
          </Row>
          <Row>
            <Cell>{i18n.t(`${TRANSLATION}.table.row4`)}</Cell>
            <Cell>{i18n.t(`${TRANSLATION}.table.premium.who_submits_applications`)}</Cell>
            <Cell color={Colors.Indigo70} bgColor={Colors.Indigo10}>
              {i18n.t(`${TRANSLATION}.table.premium_plus_auto_apply.who_submits_applications`)}
              <Img width={45} height={40} src={PeopleSrc} />
            </Cell>
          </Row>
          <Row>
            <Cell borderRadiusString="border-bottom-left-radius: 10px;">
              {i18n.t(`${TRANSLATION}.table.row5`)}
            </Cell>
            <Cell>{i18n.t(`${TRANSLATION}.table.premium.time_saved_per_20_applications`)}</Cell>
            <Cell
              color={Colors.Indigo70}
              bgColor={Colors.Indigo10}
              borderRadiusString="border-bottom-right-radius: 10px;"
            >
              {i18n.t(
                `${TRANSLATION}.table.premium_plus_auto_apply.time_saved_per_20_applications`,
              )}
              <Img width={32} height={45} src={HourGlassSrc} />
            </Cell>
          </Row>
        </Table>
        <Button
          theme={ButtonTheme.default}
          onClick={() => {
            trackInternalEvent('click_auto_apply_upsell_modal', { label })
            goToBillingPlanPage({
              referrerPage: 'auto-apply',
              upgradeURL: '/app/billing/payment',
              onSuccessRedirect: '/app/auto-apply/start',
              plan: '20_credits_autoapply',
            })
          }}
        >
          {i18n.t(`${TRANSLATION}.button`)} {getPriceButton()}
        </Button>
        <DetailsContainer>
          {isLoading ? (
            <Details>
              <PriceLoader />
            </Details>
          ) : (
            <Details
              dangerouslySetInnerHTML={{
                __html: user?.isPaidUser
                  ? i18n.t(`${TRANSLATION}.details_premium`, {
                      price: recurringAmount(),
                      subscription: subscriptionAmount(),
                      autoapply: addonAmount(),
                    })
                  : i18n.t(`${TRANSLATION}.details_non_premium`, { price: recurringAmount() }),
              }}
            />
          )}
        </DetailsContainer>
      </Container>
    </DialogModalV2>
  )
}
